// RITA-1585 — Zustand der Handbuch-Leiste je Rechner und Browser (localStorage, wie die Lage der Platzhalterleiste):
// „offen oder zu“ gilt einmal für alle Seiten, das gewählte Kapitel merkt sich jede Seite für sich (Entscheidung
// Julia 31.10.2026). Beim ersten Öffnen steht das zur Seite passende Startkapitel. Speichern ist nur eine
// Bequemlichkeit: Ohne Speicher (privates Fenster, gesperrter Speicher) läuft alles mit den Standardwerten.
import { useCallback, useState } from 'react';
import { gueltigeKapitelwahl, Handbuchseite, Kapitelwahl, STARTKAPITEL } from '../model/handbuecher';

export const HANDBUCH_OFFEN_SCHLUESSEL = 'handbuch.leiste.offen';
export const HANDBUCH_OFFEN_WERT = 'offen';

export function kapitelSchluessel(seite: Handbuchseite): string {
  return `handbuch.kapitel.${seite}`;
}

export function gemerktOffen(): boolean {
  try {
    return window.localStorage.getItem(HANDBUCH_OFFEN_SCHLUESSEL) === HANDBUCH_OFFEN_WERT;
  } catch {
    return false;
  }
}

export function offenMerken(offen: boolean): void {
  try {
    window.localStorage.setItem(HANDBUCH_OFFEN_SCHLUESSEL, offen ? HANDBUCH_OFFEN_WERT : 'zu');
  } catch {
    // stillschweigend übergehen, siehe Kopfkommentar
  }
}

/** Gemerktes Kapitel der Seite als „handbuch/kapitel“; unbekannte oder fehlende Werte → Startkapitel der Seite. */
export function gemerktesKapitel(seite: Handbuchseite): Kapitelwahl {
  try {
    const wert = window.localStorage.getItem(kapitelSchluessel(seite));
    const [handbuchId, kapitelId] = wert ? wert.split('/') : [];
    return gueltigeKapitelwahl(handbuchId, kapitelId) ?? STARTKAPITEL[seite];
  } catch {
    return STARTKAPITEL[seite];
  }
}

export function kapitelMerken(seite: Handbuchseite, wahl: Kapitelwahl): void {
  try {
    window.localStorage.setItem(kapitelSchluessel(seite), `${wahl.handbuchId}/${wahl.kapitelId}`);
  } catch {
    // stillschweigend übergehen, siehe Kopfkommentar
  }
}

interface Optionen {
  /** Kapitel aus der Adresse (Kachel, Link „im neuen Tab“): gilt vor dem gemerkten Kapitel, wird nicht gemerkt. */
  startkapitel?: Kapitelwahl | null;
}

export default function useHandbuchLeiste(seite: Handbuchseite, { startkapitel = null }: Optionen = {}) {
  const [offen, setOffen] = useState<boolean>(gemerktOffen);
  const [auswahl, setAuswahl] = useState<Kapitelwahl>(() => startkapitel ?? gemerktesKapitel(seite));

  const umschalten = useCallback(() => {
    setOffen((alt) => {
      offenMerken(!alt);
      return !alt;
    });
  }, []);
  const schliessen = useCallback(() => {
    setOffen(false);
    offenMerken(false);
  }, []);
  const waehlen = useCallback(
    (wahl: Kapitelwahl) => {
      setAuswahl(wahl);
      kapitelMerken(seite, wahl);
    },
    [seite],
  );

  return { offen, umschalten, schliessen, auswahl, waehlen };
}
